import React, { useState, useEffect, useRef } from 'react';
import { Button, CircularProgress, Typography } from '@material-ui/core/';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { notify } from 'utils';
import { useLocation, useParams } from 'react-router';
import AppLoader from '../../../../Components/AppLoader';
import { ProjectServices } from '../../../../Services/ProjectServices';
import { UserServices } from 'Services/UserServices';
import { useStyle as onboardingStyle } from '../..';
import ProjectAccordionList, { saveEditedProjects, rememberWorkingProject } from './ProjectAccordionList';
import { scrollToProjectSection } from './scrollToProject';
import { normalizeProgrammeOptions, programmeTypeLabel } from '../../../../utils/programmeOptions';

const projectServices = new ProjectServices();
const userService = new UserServices();

const useStyles = makeStyles((theme) => ({
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(2),
    },
    intro: {
        minWidth: 0,
    },
    introTitle: {
        color: '#1f2937',
        fontSize: '1.0625rem',
        fontWeight: 700,
        lineHeight: 1.35,
    },
    introText: {
        color: '#647184',
        fontSize: '0.8125rem',
        lineHeight: 1.5,
        marginTop: 2,
    },
    emptyState: {
        padding: theme.spacing(5, 2),
        textAlign: 'center',
        color: '#647184',
        border: '1px dashed #d6dde6',
        borderRadius: 8,
    },
    continueBtn: {
        minWidth: 200,
        [theme.breakpoints.down('xs')]: {
            minWidth: 0,
        },
    },
}));

export const projectRowMeta = (row) => [
    row?.startDate,
    `${row?.admin_count || 0} admins`,
    `${row?.total_users || 0} users`,
].filter(Boolean).join(' · ');

const Project = ({
    activeStep,
    handleBack,
    handleNext
}) => {
    const classes = useStyles();
    const onboardingClasses = onboardingStyle();
    const { id } = useParams();
    const location = useLocation();
    const requestedProjectId = new URLSearchParams(location.search).get('project_id');
    const requestedSection = new URLSearchParams(location.search).get('section');
    const comp_ids = localStorage.getItem('customerUUID');
    const customerId = id || comp_ids;

    const [projects, setProjectList] = useState([]);
    const [newProjects, setNewProjects] = useState([]);
    const [expandedKey, setExpandedKey] = useState(null);
    const [customerList, setCustomerList] = useState([]);
    const [projectAdminList, setProjectAdminList] = useState([]);
    const [customerAdminList, setCustomerAdminList] = useState([]);
    const [programmeOptions, setProgrammeOptions] = useState(null);
    const [isDataLoading, setIsDataLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [savingKey, setSavingKey] = useState(null);
    const editorRefs = useRef({});
    const newProjectCounter = useRef(0);
    const pendingScrollKey = useRef(null);
    const openedRequestedProject = useRef(false);

    useEffect(() => {
        if (localStorage.getItem('assign_lecense') === 'true') {
            localStorage.setItem('assign_lecense', false);
        }
        setIsDataLoading(true);
        Promise.all([
            getCustomerList(),
            getCustomerAdminList(),
            getAllProjectList(),
            getProjectAdminUser(customerId),
            getProgrammeOptions(),
        ]).finally(() => {
            setIsDataLoading(false);
        });
    // Onboarding metadata is loaded once for the fixed route customer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const key = pendingScrollKey.current;
        if (!key || !newProjects.includes(key)) return undefined;

        const frame = window.requestAnimationFrame(() => {
            if (scrollToProjectSection(key)) {
                pendingScrollKey.current = null;
            }
        });
        return () => window.cancelAnimationFrame(frame);
    }, [newProjects]);

    useEffect(() => {
        if (openedRequestedProject.current || (!requestedProjectId && requestedSection !== 'learning_hub')) return undefined;
        const target = projects.find(row => row.project_uuid === requestedProjectId)
            || (requestedSection === 'learning_hub' ? projects[0] : null);
        if (!target) return undefined;
        openedRequestedProject.current = true;
        setExpandedKey(target.project_uuid);
        if (requestedSection !== 'learning_hub') {
            const frame = window.requestAnimationFrame(() => scrollToProjectSection(target.project_uuid));
            return () => window.cancelAnimationFrame(frame);
        }
        // The Learning Hub block renders once the project detail has loaded.
        let attempts = 0;
        const timer = window.setInterval(() => {
            attempts += 1;
            const block = document.getElementById(`learning-hub-${target.project_uuid}`);
            if (block) {
                block.scrollIntoView({ behavior: 'smooth', block: 'center' });
                window.clearInterval(timer);
            } else if (attempts > 40) {
                window.clearInterval(timer);
                scrollToProjectSection(target.project_uuid);
            }
        }, 150);
        return () => window.clearInterval(timer);
    }, [projects, requestedProjectId, requestedSection]);

    const getCustomerAdminList = async () => {
        const customerARes = await projectServices.getCustomerAdminList({ customer: customerId });
        if (customerARes && customerARes.data) {
            setCustomerAdminList((customerARes.data.length) ? customerARes.data.map(itm => ({ uuid: itm.uuid, name: itm.userName })) : []);
        }
    };

    const getCustomerList = async () => {
        const customerARes = await projectServices.getCustomerList();
        if (customerARes && customerARes.data) {
            setCustomerList((customerARes.data.length) ? customerARes.data.filter(cs => (cs.uuid === customerId)) : []);
        }
    };

    const getProgrammeOptions = async () => {
        try {
            const optionsRes = await projectServices.getProgrammeOptions();
            if (!optionsRes?.status) {
                notify('error', optionsRes?.message || 'Could not load programme options.');
                return;
            }
            setProgrammeOptions(normalizeProgrammeOptions(optionsRes));
        } catch (error) {
            notify('error', error?.response?.data?.message || 'Could not load programme options.');
        }
    };

    const getAllProjectList = async () => {
        const projectsRes = await projectServices.allProjectsByCustomer(customerId);
        if (projectsRes && projectsRes.results) {
            setProjectList(projectsRes.results);
            localStorage.setItem('project_edit', false);
        }
        return projectsRes;
    };

    async function getProjectAdminUser(compId) {
        const userResult = await userService.getProjectAdminUsers(compId);
        setProjectAdminList((userResult?.data && userResult?.data?.map(itm => ({ name: itm.userName, id: itm.uuid }))) || []);
        return userResult;
    }

    const handleProjectAdminCreated = async (newAdmin, email) => {
        const userResult = await getProjectAdminUser(customerId);
        const refreshedList = (userResult?.data && userResult?.data?.map(itm => ({
            name: itm.userName,
            id: itm.uuid,
        }))) || [];

        const emailKey = String(email || newAdmin?.name || '').toLowerCase();
        const match = refreshedList.find(
            (itm) => itm.id === newAdmin?.id
                || String(itm.name || '').toLowerCase() === emailKey
        );
        const selectedId = match?.id || newAdmin?.id || '';

        if (selectedId && !refreshedList.some((itm) => itm.id === selectedId)) {
            setProjectAdminList((prev) => {
                if (prev.some((itm) => itm.id === selectedId)) return prev;
                return [...prev, { name: newAdmin?.name || email, id: selectedId }];
            });
        }
        return selectedId;
    };

    const handleCreateNew = () => {
        newProjectCounter.current += 1;
        const key = `new-${newProjectCounter.current}`;
        pendingScrollKey.current = key;
        setNewProjects(prev => [...prev, key]);
        setExpandedKey(key);
    };

    const handleDiscard = (key) => {
        setNewProjects(prev => prev.filter(item => item !== key));
        if (expandedKey === key) setExpandedKey(null);
    };

    const items = [
        ...projects.map(row => ({
            key: row.project_uuid,
            uuid: row.project_uuid,
            title: row.project_name || 'Untitled project',
            meta: projectRowMeta(row),
            status: row.status,
            programmeLabel: programmeTypeLabel(row),
        })),
        ...newProjects.map((key, index) => ({
            key,
            uuid: null,
            title: `New project ${projects.length + index + 1}`,
            meta: 'Not saved yet',
            isNew: true,
        })),
    ];

    const handleSaveProject = async (key) => {
        const editor = editorRefs.current[key];
        if (!editor) return;
        setSavingKey(key);
        try {
            const result = await editor.save();
            if (!result.ok) return;
            if (!result.saved) {
                notify('info', 'No changes to save for this project.');
                return;
            }
            notify('success', 'Project saved.');
            localStorage.setItem('refreshlic', true);
            const projectsRes = await getAllProjectList();
            const savedRows = projectsRes?.results || projects;
            rememberWorkingProject(result.uuid, savedRows.map(row => row.project_uuid).filter(Boolean));
            if (newProjects.includes(key)) {
                setNewProjects(prev => prev.filter(item => item !== key));
                if (result.uuid) setExpandedKey(result.uuid);
            }
        } finally {
            setSavingKey(null);
        }
    };

    const handleContinue = async () => {
        if (!items.length) {
            notify("error", "Create a project before continuing to Allocation.");
            return;
        }
        setSubmitLoading(true);
        try {
            const result = await saveEditedProjects(items, editorRefs);
            if (!result.ok) {
                if (result.savedCount) {
                    await getAllProjectList();
                    setNewProjects(prev => prev.filter(key => !result.savedKeys.includes(key)));
                }
                setExpandedKey(result.failedKey);
                return;
            }
            const projectsRes = result.savedCount ? await getAllProjectList() : null;
            const savedRows = projectsRes?.results || projects;
            if (result.savedCount) {
                localStorage.setItem('refreshlic', true);
                notify("success", result.savedCount === 1 ? 'Project saved.' : `${result.savedCount} projects saved.`);
            }
            setNewProjects([]);
            rememberWorkingProject(result.lastSavedUuid, savedRows.map(row => row.project_uuid).filter(Boolean));
            handleNext();
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <>
            <div className={classes.toolbar}>
                <div className={classes.intro}>
                    <Typography component="h2" className={classes.introTitle}>Projects and project administrators</Typography>
                    <Typography className={classes.introText}>
                        Open a project to edit its details. Licences are allocated to projects on the next step.
                    </Typography>
                </div>
                <Button
                    variant="contained"
                    color="primary"
                    type="button"
                    startIcon={<AddIcon />}
                    onClick={handleCreateNew}
                    disabled={isDataLoading || submitLoading}
                >
                    Create Project
                </Button>
            </div>
            {isDataLoading ? (
                <AppLoader minHeight={240} />
            ) : items.length ? (
                <ProjectAccordionList
                    items={items}
                    section="details"
                    expandedKey={expandedKey}
                    onExpand={setExpandedKey}
                    editorRefs={editorRefs}
                    onDiscard={handleDiscard}
                    customerId={customerId}
                    customerList={customerList}
                    customerAdminList={customerAdminList}
                    projectAdminList={projectAdminList}
                    onProjectAdminCreated={handleProjectAdminCreated}
                    programmeOptions={programmeOptions}
                    onSave={handleSaveProject}
                    savingKey={savingKey}
                />
            ) : (
                <div className={classes.emptyState}>
                    No projects yet. Use Create Project to add the first one.
                </div>
            )}
            <div className={onboardingClasses.buttons}>
                <div className={onboardingClasses.wrapper}>
                    {activeStep !== 0 && (
                        <Button
                            type="button"
                            onClick={handleBack}
                            variant="outlined"
                            color="default"
                            disabled={submitLoading}
                            className={onboardingClasses.cancelBtn}
                        >
                            Back
                        </Button>
                    )}
                    <Button
                        type="button"
                        onClick={handleContinue}
                        variant="contained"
                        color="primary"
                        disabled={isDataLoading || submitLoading || !!savingKey}
                        endIcon={submitLoading ? null : <ArrowForwardIcon fontSize="small" />}
                        className={`${onboardingClasses.button} ${classes.continueBtn}`}
                    >
                        {submitLoading ? <CircularProgress size={18} /> : 'Continue to Allocation'}
                    </Button>
                </div>
            </div>
        </>
    );
};

export default Project;
